import api from "./api";
import { gvmEditPath } from "./apiPaths";
import type {
  EditUploadConfirmBody,
  EditUploadConfirmed,
  EditUploadSlot,
  FacilityEditViewResponse,
  MultiFacilityProfileResponse,
  MyFacilitiesResponse,
  ReverifyFacilityBody,
  ReverifyFacilityResponse,
  SubmitEditRequestBody,
  SubmitEditResponse,
  VendorProfileFacility,
} from "@/pages/VendorProfile/types";

export type VendorProfileAuthMode = "vendor" | "bouncer";

function configFor(auth: VendorProfileAuthMode): Record<string, unknown> {
  return auth === "vendor" ? { useVendorToken: true } : {};
}

async function getMyFacilities(
  auth: VendorProfileAuthMode = "bouncer",
): Promise<MyFacilitiesResponse> {
  return (await api.get(
    gvmEditPath("/me/facilities"),
    configFor(auth),
  )) as MyFacilitiesResponse;
}

async function getReviewedFacilities(): Promise<MyFacilitiesResponse> {
  return (await api.get(
    gvmEditPath("/me/reviewed-facilities"),
  )) as MyFacilitiesResponse;
}

async function getFacilityEditView(
  facilityId: string,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<FacilityEditViewResponse> {
  return (await api.get(
    gvmEditPath(`/facilities/${facilityId}/edit-view`),
    configFor(auth),
  )) as FacilityEditViewResponse;
}

function combine(
  responses: FacilityEditViewResponse[],
): MultiFacilityProfileResponse {
  return {
    vendor: responses.find((r) => r.vendor)?.vendor,
    field_governance: Object.fromEntries(
      responses.map((r) => [r.profile.id, r.field_governance ?? {}]),
    ),
    facilities: responses.map((r) => ({
      profile: r.profile,
      documents: r.documents ?? [],
      contact_emails: r.contact_emails ?? {},
      operational: r.operational ?? null,
      change_requests: r.change_requests ?? [],
    })),
  };
}

export async function getVendorProfileByFacilityIds(
  facilityIds: string[],
  auth: VendorProfileAuthMode = "bouncer",
): Promise<MultiFacilityProfileResponse> {
  if (facilityIds.length === 0) {
    throw new Error("No facilities are linked to this account.");
  }
  return combine(
    await Promise.all(facilityIds.map((id) => getFacilityEditView(id, auth))),
  );
}

export async function getMyVendors(
  auth: VendorProfileAuthMode = "bouncer",
): Promise<VMVendorSummary[]> {
  const list = await getMyFacilities(auth);
  return summariseByVendor(list.facilities ?? []);
}

export interface VMVendorSummary {
  vendor_id: string;
  legal_name: string;
  facilities_count: number;
  facility_ids: string[];
  primary_facility_id: string;
  primary_gst?: string | null;
  primary_city?: string | null;
  status: string;
}

/** A verification outcome VIP already holds for the vendor behind a pending review (ENG-27734). */
export interface PendingReviewFlag {
  type: "PAN" | "GST" | string;
  status: string;
}

/** One open review assignment of the signed-in reviewer (GET /me/pending-reviews). */
export interface PendingReviewItem {
  source: "ONBOARDING" | "CHANGE_REQUEST" | string;
  task_id: string;
  task_visible_id?: string;
  reference: string;
  code?: string;
  stage: string;
  stage_label: string;
  assigned_at: string;
  waiting_seconds: number;
  review_url?: string;
  changed_fields?: string[];
  flags: PendingReviewFlag[];
}

interface PendingReviewsResponse {
  items?: PendingReviewItem[];
}

/** The caller's open onboarding / change-request review tasks, oldest first (ENG-27734). */
export async function getMyPendingReviews(): Promise<PendingReviewItem[]> {
  const res = (await api.get(
    gvmEditPath("/me/pending-reviews"),
  )) as PendingReviewsResponse;
  return res?.items ?? [];
}

export async function getVMReviewedVendors(): Promise<VMVendorSummary[]> {
  const list = await getReviewedFacilities();
  return summariseByVendor(list.facilities ?? []);
}

function summariseByVendor(
  facilities: VendorProfileFacility[],
): VMVendorSummary[] {
  const byVendor = new Map<string, VMVendorSummary>();
  for (const f of facilities) {
    const existing = byVendor.get(f.vendor_id);
    if (existing) {
      existing.facilities_count += 1;
      existing.facility_ids.push(f.id);
      if (f.is_primary) {
        existing.primary_facility_id = f.id;
        existing.primary_gst = f.gst ?? null;
        existing.primary_city = f.billing_city ?? null;
        existing.status = f.status;
      }
    } else {
      byVendor.set(f.vendor_id, {
        vendor_id: f.vendor_id,
        legal_name: f.legal_name,
        facilities_count: 1,
        facility_ids: [f.id],
        primary_facility_id: f.id,
        primary_gst: f.is_primary ? f.gst ?? null : null,
        primary_city: f.is_primary ? f.billing_city ?? null : null,
        status: f.status,
      });
    }
  }
  return [...byVendor.values()];
}

export async function getVMVendorProfile(
  vendorId: string,
): Promise<MultiFacilityProfileResponse> {
  const list = await getReviewedFacilities();
  const facilities = (list.facilities ?? []).filter(
    (f) => f.vendor_id === vendorId,
  );
  if (facilities.length === 0) {
    throw new Error("This vendor isn't in your reviewed list.");
  }
  const responses = await Promise.all(
    facilities.map((f) => getFacilityEditView(f.id, "bouncer")),
  );
  return combine(responses);
}

export async function submitEditRequest(
  facilityId: string,
  body: SubmitEditRequestBody,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<SubmitEditResponse> {
  return (await api.post(
    gvmEditPath(`/facilities/${facilityId}/edit-requests`),
    body,
    configFor(auth),
  )) as SubmitEditResponse;
}

export async function mintEditUpload(
  facilityId: string,
  fileName: string,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<EditUploadSlot> {
  const slot = (await api.post(
    gvmEditPath(`/facilities/${facilityId}/edit-uploads`),
    { file_name: fileName },
    configFor(auth),
  )) as EditUploadSlot | null;
  if (!slot?.url || !slot?.attachment_id) {
    throw new Error("mintEditUpload: malformed response");
  }
  return slot;
}

export async function confirmEditUpload(
  facilityId: string,
  body: EditUploadConfirmBody,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<EditUploadConfirmed> {
  const confirmed = (await api.post(
    gvmEditPath(`/facilities/${facilityId}/edit-uploads/confirm`),
    body,
    configFor(auth),
  )) as EditUploadConfirmed | null;
  if (!confirmed?.attachment_id) {
    throw new Error("confirmEditUpload: malformed response");
  }
  return confirmed;
}

export interface OtpSendRequest {
  channel: "email" | "mobile";
  target: string;
  country_code?: string;
  facility_id?: string;
  field?: string;
}

export interface OtpSendResponse {
  request_id: string;
}

export interface OtpVerifyRequest {
  request_id: string;
  otp: string;
}

export interface OtpVerifyResponse {
  verified: boolean;
}

export async function sendOtp(
  body: OtpSendRequest,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<OtpSendResponse> {
  return (await api.post(
    gvmEditPath("/otp/send"),
    body,
    configFor(auth),
  )) as OtpSendResponse;
}

export async function verifyOtp(
  body: OtpVerifyRequest,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<OtpVerifyResponse> {
  return (await api.post(
    gvmEditPath("/otp/verify"),
    body,
    configFor(auth),
  )) as OtpVerifyResponse;
}

export async function getDocumentDownloadUrl(
  documentId: number | string,
  auth: VendorProfileAuthMode = "bouncer",
): Promise<string> {
  const response = (await api.get(
    gvmEditPath(`/documents/${encodeURIComponent(String(documentId))}/download-url`),
    configFor(auth),
  )) as { download_url?: string } | null;
  const url = response?.download_url;
  if (!url) throw new Error("getDocumentDownloadUrl: malformed response");
  return url;
}

export async function reverifyFacility(
  facilityId: string,
  body: ReverifyFacilityBody = {},
  auth: VendorProfileAuthMode = "bouncer",
): Promise<ReverifyFacilityResponse> {
  return (await api.post(
    gvmEditPath(`/facilities/${facilityId}/reverify`),
    body,
    configFor(auth),
  )) as ReverifyFacilityResponse;
}

const vendorProfileService = {
  getVendorProfileByFacilityIds,
  getMyVendors,
  getMyFacilities,
  getReviewedFacilities,
  getFacilityEditView,
  getVMReviewedVendors,
  getMyPendingReviews,
  getVMVendorProfile,
  submitEditRequest,
  mintEditUpload,
  confirmEditUpload,
  reverifyFacility,
  sendOtp,
  verifyOtp,
  getDocumentDownloadUrl,
};
export default vendorProfileService;
